import { forwardRef, useEffect, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Col from 'react-bootstrap/Col';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import InteractiveElement from '../../../atoms/interactive-element';
import findIndex from 'lodash/findIndex';
import { useSwipeable } from 'react-swipeable';

import Icon from '../../../atoms/icon';
import styles from './page-side-rail.module.scss';
import usePageSideRail from '../../../../stores/profile/modules/use-page-side-rail';
import useScreenerSideRail from '../../../../stores/screener/use-screener-side-rail';
import useSideRailContext from '../../../../stores/profile/use-side-rail-context';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';

const className = augmentClassName(styles);

const PageSideRail = ({ appContext = 'profile', printDisclosures }) => {
  const [disclosureIndex, setDisclosureIndex] = useState('0');
  const [glossaryIndex, setGlossaryIndex] = useState('0');
  const [tagDefinitionsIndex, setTagDefinitionsIndex] = useState('0');
  const [previousFocus, setPreviousFocus] = useState(null);
  const [scrollTerm, setScrollTerm] = useState('');

  const isScreener = appContext === 'screener';
  let pageSideRailContext;
  if (isScreener) {
    pageSideRailContext = useScreenerSideRail;
  } else {
    pageSideRailContext = usePageSideRail;
  }

  const {
    disclosures: disclosureList,
    glossary: glossaryList,
    tagDefinitions: tagDefinitionsList,
    pageOffsetsTop
  } = pageSideRailContext();

  const {
    disclosures: disclosureToggle,
    glossary: glossaryToggle,
    tagDefinitions: tagDefinitionToggle,
    hide,
    isVisible,
    scrollToModule,
    show,
    tabActive,
    toggle
  } = useSideRailContext();

  const handleSwipe = useSwipeable({
    onSwipedLeft: show,
    onSwipedRight: hide
  });

  const renderDisclosures = tabActive === 'disclosures';
  const renderGlossary = tabActive === 'glossary';
  const renderTagDefinitions = tabActive === 'tagDefinitions';

  const componentOffsets = {};
  if (pageOffsetsTop) componentOffsets.top = pageOffsetsTop;

  useEffect(() => {
    if (typeof(scrollToModule) === 'string') {
      const index = renderDisclosures
        ? findIndex(disclosureList, item => (item.component?.toLowerCase() || '') === scrollToModule?.toLowerCase())
        : findIndex(glossaryList, item => (item.term?.toLowerCase() || '') === scrollToModule?.toLowerCase());
      if (index >= 0) {
        renderDisclosures
          ? setDisclosureIndex(index.toString())
          : setGlossaryIndex(index.toString());
        setScrollTerm(renderDisclosures ? disclosureList[index].module : glossaryList[index].term);
      } else {
        renderDisclosures && setDisclosureIndex('0');
      }
    }
  }, [disclosureList, glossaryList, renderGlossary, renderDisclosures, scrollToModule]);

  const hideWrapper = () => {
    previousFocus.focus();
    hide();
  };

  /* For ADA Useability. This locks the tab navigation into the open modal */
  const elementToFocusOnOpen = useRef(null);
  const lastElementInModalToFocus = useRef(null);

  const handleKeyPress = e => {
    if (e.keyCode === 9) {
      if (e.shiftKey && document.activeElement === elementToFocusOnOpen.current) {
        e.preventDefault();
        lastElementInModalToFocus.current.focus();
      } else if (!e.shiftKey && document.activeElement === lastElementInModalToFocus.current) {
        e.preventDefault();
        elementToFocusOnOpen.current.focus();
      }
    }
  };

  useEffect(() => {
    window.removeEventListener('keydown', handleKeyPress);
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setPreviousFocus(document.activeElement);
      elementToFocusOnOpen && elementToFocusOnOpen.current && elementToFocusOnOpen.current.focus();
    }
  }, [elementToFocusOnOpen, isVisible]);
  /* end ADA */

  //Allows links rendered in disclosure to retain the target='_blank' attribute after sanitization
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    // set all elements owning target to target=_blank
    if ('target' in node) {
      node.setAttribute('target', '_blank');
      // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  const screenerAnalyticsLink1 = {};
  screenerAnalyticsLink1['data-analytics-linkname'] = 'rightrail:GlossaryCollapse';
  const screenerAnalyticsLink2 = {};
  screenerAnalyticsLink2['data-analytics-linkname'] = 'rightrail:FilterGlossary';
  const screenerAnalyticsLink3 = {};
  screenerAnalyticsLink3['data-analytics-linkname'] = 'rightrail:FilterGlossaryTags';
  const screenerAnalyticsLink4 = {};
  screenerAnalyticsLink4['data-analytics-linkname'] = 'rightrail:GlossaryExpand';

  const screenerAnalyticsCommon = {};
  screenerAnalyticsCommon['data-analytics-componenttype'] = 'Page';
  screenerAnalyticsCommon['data-analytics-section'] = 'component';

  const profileAnalyticsCommon = {};
  profileAnalyticsCommon['data-analytics-componenttype'] = 'interactive-graphic-component';
  profileAnalyticsCommon['data-analytics-section'] = 'component';
  profileAnalyticsCommon['data-analytics-id'] = 'IG_PageSideRail';

  return (
    <aside
      // TIAA Analytics Tracking
      {...(isScreener && { ...screenerAnalyticsLink4 })}
      {...(isScreener && { ...screenerAnalyticsCommon })}
      {...(!isScreener && { ...profileAnalyticsCommon })}
      // END TIAA Analytics Tracking
      {...className(
        'page-side-rail',
        isVisible ? 'open' : null,
        isScreener ? 'screener-glossary' : {}
      )}
      style={componentOffsets}
      role="contentinfo"
      aria-label="page siderail disclosures"
    >
      <div {...className('rail-divider')}>
        <InteractiveElement {...className('handle')} onClick={toggle} {...handleSwipe}>
          <Icon.ChevronLeft role="img" aria-label="Open Glossary" />
        </InteractiveElement>
        <InteractiveElement {...className('divider')} onClick={toggle}></InteractiveElement>
      </div>
      <div {...className('rail-content')} {...handleSwipe}>
        <div {...className('header')}>
          <Row>
            <Col>
              <div
                {...className('close', isScreener ? 'screener-close-plan-details' : {})}
                // TIAA Analytics Tracking
                {...(isScreener && { ...screenerAnalyticsLink1 })}
                {...(isScreener && { ...screenerAnalyticsCommon })}
                // END TIAA Analytics Tracking
              >
                <Icon.Times
                  onClick={hideWrapper}
                  role="img"
                  aria-label="Close Glossary"
                  tabIndex="0"
                  ref={elementToFocusOnOpen}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div {...className('menu')}>
                <div
                  {...className('menu-item', isScreener ? 'screener-glossary' : {})}
                  // TIAA Analytics Tracking
                  {...(isScreener && { ...screenerAnalyticsLink2 })}
                  {...(isScreener && { ...screenerAnalyticsCommon })}
                  // END TIAA Analytics Tracking
                >
                  <Icon.Glossary
                    role="img"
                    aria-label="Glossary"
                    onClick={glossaryToggle.show}
                    tabIndex="0"
                    {...className(renderGlossary ? 'active' : null)}
                  />
                  {renderGlossary && <div {...className('sub-title-text')}> Glossary</div>}
                </div>
                {!isScreener && (
                  <div {...className('menu-item')}>
                    <Icon.Disclosures
                      aria-label="Disclosures"
                      onClick={disclosureToggle.show}
                      {...className(renderDisclosures ? 'active' : null)}
                    />
                    {renderDisclosures && <div {...className('sub-title-text')}>Disclosures</div>}
                  </div>
                )}
                <div
                  {...className('menu-item', isScreener ? 'screener-tags' : {})}
                  // TIAA Analytics Tracking
                  {...(isScreener && { ...screenerAnalyticsLink3 })}
                  {...(isScreener && { ...screenerAnalyticsCommon })}
                  // END TIAA Analytics Tracking
                >
                  <Icon.TagGlossary
                    role="img"
                    aria-label="Tags"
                    onClick={tagDefinitionToggle.show}
                    {...className(renderTagDefinitions ? 'active' : null)}
                  />
                  {renderTagDefinitions && <div {...className('sub-title-text')}>TAGS</div>}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div {...className('body')}>
          {renderDisclosures && (
            <Content
              contentIndex={disclosureIndex}
              data={disclosureList}
              setContentIndex={setDisclosureIndex}
              type={'disclosure'}
              isScreener={isScreener}
              ref={lastElementInModalToFocus}
              scrollTerm={scrollTerm}
            />
          )}
          {renderGlossary && (
            <Content
              contentIndex={glossaryIndex}
              data={glossaryList}
              setContentIndex={setGlossaryIndex}
              type={'glossary'}
              isScreener={isScreener}
              ref={lastElementInModalToFocus}
              scrollTerm={scrollTerm}
            />
          )}
          {renderTagDefinitions && (
            <Content
              contentIndex={tagDefinitionsIndex}
              data={tagDefinitionsList}
              setContentIndex={setTagDefinitionsIndex}
              type={'tagDefinitions'}
              isScreener={isScreener}
              ref={lastElementInModalToFocus}
            />
          )}
        </div>
      </div>
    </aside>
  );
};

PageSideRail.defaultProps = {
  printDisclosures: true
};

PageSideRail.propTypes = {
  appContext: PropTypes.string,
  children: PropTypes.node,
  printDisclosures: PropTypes.bool
};

const Content = forwardRef(({ contentIndex, data, setContentIndex, type, isScreener, scrollTerm }, ref) => {
  const renderDisclosures = type === 'disclosure';
  const renderGlossary = type === 'glossary';
  const renderTagDefinitions = type === 'tagDefinitions';

  useEffect(() => {
    if (renderDisclosures || renderGlossary) {
      const itemClass = className.string('accordion-item');

      const activeItem = document.querySelector(`.${itemClass}[data-key="${scrollTerm?.toLowerCase()}-${contentIndex}"]`);
      if (activeItem) {
        setTimeout(() => {
          activeItem.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, [contentIndex, renderDisclosures, renderGlossary, scrollTerm]);

  const screenerAnalyticsTags = {};
  screenerAnalyticsTags['data-analytics-linkname'] = 'rightrail:GlossaryAccordion';
  screenerAnalyticsTags['data-analytics-section'] = 'component';
  screenerAnalyticsTags['data-analytics-componenttype'] = 'Page';

  return (
    <Accordion
      activeKey={contentIndex}
      defaultActiveKey={'0'}
      onSelect={setContentIndex}
      {...className('accordion', isScreener ? 'screener-accordion' : {})}
      // TIAA Analytics Tracking
      {...(isScreener && { ...screenerAnalyticsTags })}
      // END TIAA Analytics Tracking
    >
      {data.map((item, index) => {
        //TIAA-12642 | Making index a string, because that is the proper type for eventKeys, and it was causing a bug.
        const indexString = index.toString();
        const isActive = contentIndex === indexString;
        const keyPrefix = item.term || item.module || '';
        return (
          <div
            data-key={`${keyPrefix?.toLowerCase() || ''}-${index}`}
            key={`${keyPrefix}-${index}`}
            {...className('accordion-item', isActive ? 'active' : null)}
          >
            <div {...className('item-border')}>
              <div>
                <Accordion.Toggle
                  eventKey={indexString}
                  aria-label={
                    isActive
                      ? 'Collapse additional information'
                      : 'Expand for additional information'
                  }
                  ref={data.length - 1 === index ? ref : null}
                  {...className('item-header', isActive ? 'active' : null)}
                >
                  <div {...className('d-flex', 'text-left')}>
                    {isActive ? (
                      <Icon.ChevronDown
                        height="12px"
                        width="14px"
                        role="img"
                        aria-label="Collapse"
                        {...className('flex-shrink-0')}
                      />
                    ) : (
                      <Icon.ChevronRight
                        height="14px"
                        width="8px"
                        role="img"
                        aria-label="Expand"
                        {...className('flex-shrink-0')}
                      />
                    )}
                    <>
                      {renderDisclosures && <span>{item.module}</span>}
                      {renderGlossary && <span>{item.term}</span>}
                      {renderTagDefinitions && <span>{item.tag}</span>}
                    </>
                  </div>
                </Accordion.Toggle>
                <Accordion.Collapse eventKey={indexString} {...className('item-content')}>
                  <>
                    {renderDisclosures && (
                      <dl>
                        {(item.disclosures || []).map((item2, index2) => {
                          const sanitizedDisclosure = DOMPurify.sanitize(
                            item2.disclosure,
                            { USE_PROFILES: { html: true } } // allow all safe HTML elements but neither SVG nor MathML
                          );
                          return (
                            <div
                              data-key={`${item2.term || ''}-${index2}`}
                              key={`${item2.term || ''}-${index2}`}
                            >
                              <dt>{item2.term}</dt>
                              <dd
                                dangerouslySetInnerHTML={{
                                  __html: sanitizedDisclosure
                                }}
                              />
                            </div>
                          );
                        })}
                      </dl>
                    )}
                    {renderGlossary && <span>{item.content}</span>}
                    {renderTagDefinitions && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item.definition
                        }}
                      />
                    )}
                  </>
                </Accordion.Collapse>
              </div>
            </div>
          </div>
        );
      })}
    </Accordion>
  );
});
Content.displayName = 'Disclosures';

Content.defaultProps = {
  data: []
};

Content.propTypes = {
  contentIndex: PropTypes.string,
  data: PropTypes.array.isRequired,
  setContentIndex: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['disclosure', 'glossary', 'tagDefinitions']).isRequired,
  isScreener: PropTypes.bool,
  scrollTerm: PropTypes.string
};

export const DisclosurePrint = ({ disclosureList, avoidPageBreak, admasterNumber }) => {
  return (
    <div
      {...className(
        'pt-3',
        'd-none',
        'd-print-block',
        'print-disclosures',
        avoidPageBreak ? 'avoid-page-break' : ''
      )}
    >
      {disclosureList.length > 0 &&
        disclosureList.map(disclosureByComponent => {
          return disclosureByComponent?.disclosures?.map?.((disclosure, index) => {
            const sanitizedDisclosure = DOMPurify.sanitize(disclosure?.disclosure, {
              USE_PROFILES: { html: true }
            }); // allow all safe HTML elements but neither SVG nor MathML
            const sanitizedTerm = DOMPurify.sanitize(
              disclosure?.term,
              { USE_PROFILES: { html: true } } // allow all safe HTML elements but neither SVG nor MathML
            );
            return (
              <div
                data-key={`${sanitizedTerm || ''}-${index}`}
                key={`${sanitizedTerm || ''}-${index}`}
              >
                <p
                  {...className('mb-2', 'sub-title-print')}
                  dangerouslySetInnerHTML={{ __html: sanitizedTerm }}
                ></p>
                <p dangerouslySetInnerHTML={{ __html: sanitizedDisclosure }}></p>
              </div>
            );
          });
        })}
      {admasterNumber && <div {...className('admaster-number')}>{admasterNumber}</div>}
    </div>
  );
};

DisclosurePrint.defaultProps = {
  addmasterNumber: '',
  avoidPageBreak: false,
  disclosureList: []
};

DisclosurePrint.propTypes = {
  admasterNumber: PropTypes.string,
  avoidPageBreak: PropTypes.bool,
  disclosureList: PropTypes.array.isRequired
};

export default PageSideRail;
